/**
 * Currency Formatting Utility
 *
 * Provides centralized currency formatting for the application.
 * Replaces hardcoded EUR formatting throughout the codebase.
 */

// Currency symbol mapping for common currencies
const CURRENCY_SYMBOLS = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    CHF: 'CHF',
    JPY: '¥',
    CNY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    HRK: 'kn',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    RSD: 'дин.',
    BAM: 'KM',
    MKD: 'ден',
    ALL: 'L',
    TRY: '₺',
    RUB: '₽',
    UAH: '₴',
    INR: '₹',
    BRL: 'R$',
    MXN: 'MX$',
    ARS: 'AR$',
    CLP: 'CL$',
    COP: 'CO$',
    PEN: 'S/',
    VES: 'Bs.',
    ZAR: 'R',
    EGP: 'E£',
    AED: 'د.إ',
    SAR: '﷼',
    ILS: '₪',
    THB: '฿',
    SGD: 'S$',
    MYR: 'RM',
    IDR: 'Rp',
    PHP: '₱',
    VND: '₫',
    KRW: '₩',
    TWD: 'NT$',
    HKD: 'HK$',
    NZD: 'NZ$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
};

// Locale mapping for currencies
const CURRENCY_LOCALES = {
    EUR: 'de-DE',
    USD: 'en-US',
    GBP: 'en-GB',
    CHF: 'de-CH',
    JPY: 'ja-JP',
    CNY: 'zh-CN',
    HRK: 'hr-HR',
    PLN: 'pl-PL',
    CZK: 'cs-CZ',
    HUF: 'hu-HU',
    RON: 'ro-RO',
    BGN: 'bg-BG',
    RSD: 'sr-RS',
    TRY: 'tr-TR',
    RUB: 'ru-RU',
    UAH: 'uk-UA',
    INR: 'en-IN',
    BRL: 'pt-BR',
};

/**
 * Default currency code (EUR as base currency)
 */
export const DEFAULT_CURRENCY = 'EUR';

/**
 * Format a numeric value as currency.
 *
 * @param {number|string} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'EUR', 'USD')
 * @param {Object} options - Additional formatting options
 * @param {string} options.locale - Override the default locale for the currency
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 2)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @param {boolean} options.showSymbol - Whether to show the currency symbol (default: true)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, currencyCode = DEFAULT_CURRENCY, options = {}) {
    const {
        locale,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
        showSymbol = true,
        compact = false,
    } = options;

    // Handle null, undefined, or invalid values
    if (value === null || value === undefined || value === '') {
        return showSymbol ? `${getCurrencySymbol(currencyCode)}0.00` : '0.00';
    }

    // Convert to number if string
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    // Handle NaN
    if (isNaN(numericValue)) {
        return showSymbol ? `${getCurrencySymbol(currencyCode)}0.00` : '0.00';
    }

    // Determine locale
    const formatLocale = locale || CURRENCY_LOCALES[currencyCode] || 'en-US';

    try {
        const formatOptions = {
            style: showSymbol ? 'currency' : 'decimal',
            currency: currencyCode,
            minimumFractionDigits,
            maximumFractionDigits,
        };

        if (compact) {
            formatOptions.notation = 'compact';
            formatOptions.compactDisplay = 'short';
        }

        return numericValue.toLocaleString(formatLocale, formatOptions);
    } catch (error) {
        // Fallback for unsupported currencies
        const symbol = showSymbol ? getCurrencySymbol(currencyCode) : '';
        return `${symbol}${numericValue.toFixed(minimumFractionDigits)}`;
    }
}

/**
 * Format currency with explicit symbol placement.
 *
 * @param {number|string} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {Object} options - Formatting options
 * @returns {string} Formatted string with symbol
 */
export function formatCurrencyWithSymbol(value, currencyCode = DEFAULT_CURRENCY, options = {}) {
    return formatCurrency(value, currencyCode, { ...options, showSymbol: true });
}

/**
 * Format currency without symbol (just the number).
 *
 * @param {number|string} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {Object} options - Formatting options
 * @returns {string} Formatted number string
 */
export function formatCurrencyValue(value, currencyCode = DEFAULT_CURRENCY, options = {}) {
    return formatCurrency(value, currencyCode, { ...options, showSymbol: false });
}

/**
 * Get the symbol for a currency code.
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currencyCode = DEFAULT_CURRENCY) {
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
}

/**
 * Format a range of currency values.
 *
 * @param {number} minValue - Minimum value
 * @param {number} maxValue - Maximum value
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted range string
 */
export function formatCurrencyRange(minValue, maxValue, currencyCode = DEFAULT_CURRENCY) {
    const formattedMin = formatCurrency(minValue, currencyCode);
    const formattedMax = formatCurrency(maxValue, currencyCode);
    return `${formattedMin} - ${formattedMax}`;
}

/**
 * Format currency for display in tables/lists (compact format).
 *
 * @param {number|string} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Compact formatted currency string
 */
export function formatCurrencyCompact(value, currencyCode = DEFAULT_CURRENCY) {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numericValue)) {
        return `${getCurrencySymbol(currencyCode)}0`;
    }

    // Use compact notation for values >= 10,000
    if (Math.abs(numericValue) >= 10000) {
        return formatCurrency(value, currencyCode, { compact: true });
    }

    return formatCurrency(value, currencyCode);
}

/**
 * Parse a formatted currency string back to a number.
 *
 * @param {string} formattedValue - The formatted currency string
 * @returns {number} Numeric value
 */
export function parseCurrencyString(formattedValue) {
    if (!formattedValue || typeof formattedValue !== 'string') {
        return 0;
    }

    // Remove currency symbols, spaces, and thousand separators
    const cleanedValue = formattedValue
        .replace(/[^\d.,-]/g, '') // Remove non-numeric except . , -
        .replace(/,/g, '.') // Replace comma with period (European format)
        .replace(/\.(?=.*\.)/g, ''); // Remove all but last period

    return parseFloat(cleanedValue) || 0;
}

/**
 * Get display name for a currency.
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} locale - Locale for display name
 * @returns {string} Display name (e.g., "Euro", "US Dollar")
 */
export function getCurrencyDisplayName(currencyCode, locale = 'en') {
    try {
        const displayNames = new Intl.DisplayNames([locale], { type: 'currency' });
        return displayNames.of(currencyCode);
    } catch {
        return currencyCode;
    }
}

/**
 * Check if a currency code is valid/supported.
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {boolean} Whether the currency is supported
 */
export function isSupportedCurrency(currencyCode) {
    try {
        new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode });
        return true;
    } catch {
        return false;
    }
}

/**
 * Create a currency formatter function bound to a specific currency.
 * Useful for components that always display the same currency.
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {Object} defaultOptions - Default formatting options
 * @returns {Function} Bound formatter function
 */
export function createCurrencyFormatter(currencyCode = DEFAULT_CURRENCY, defaultOptions = {}) {
    return (value, options = {}) => formatCurrency(value, currencyCode, { ...defaultOptions, ...options });
}

export default {
    formatCurrency,
    formatCurrencyWithSymbol,
    formatCurrencyValue,
    formatCurrencyCompact,
    formatCurrencyRange,
    getCurrencySymbol,
    getCurrencyDisplayName,
    parseCurrencyString,
    isSupportedCurrency,
    createCurrencyFormatter,
    DEFAULT_CURRENCY,
};
